<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';

const { t } = useI18n();
const { contentAttributes } = useMessageContext();

const data = computed(() => contentAttributes.value?.data || {});
const response = computed(() => data.value.response);
const isPermanent = computed(() => data.value.is_permanent);

const isAccepted = computed(() => response.value === 'accept');

const expirationText = computed(() => {
  const timestamp = data.value.expiration_timestamp;
  if (!timestamp) return '';
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const responseLabel = computed(() => {
  if (!isAccepted.value) {
    return t('CONVERSATION.CALL_PERMISSION.DECLINED');
  }
  if (isPermanent.value) {
    return t('CONVERSATION.CALL_PERMISSION.ALLOWED_PERMANENT');
  }
  return t('CONVERSATION.CALL_PERMISSION.ALLOWED_TEMPORARY');
});
</script>

<template>
  <BaseBubble class="p-0 border-none" hide-meta>
    <div class="flex overflow-hidden flex-col w-full max-w-xs rounded-xl">
      <!-- Card body -->
      <div class="flex gap-3 items-center p-3 bg-n-slate-3 dark:bg-n-slate-3">
        <div
          class="flex justify-center items-center rounded-full size-10 shrink-0"
          :class="isAccepted ? 'bg-n-teal-3' : 'bg-n-ruby-3'"
        >
          <Icon
            class="size-5"
            :icon="isAccepted ? 'i-ph-phone' : 'i-ph-phone-x'"
            :class="isAccepted ? 'text-n-teal-11' : 'text-n-ruby-11'"
          />
        </div>

        <div class="flex overflow-hidden flex-col flex-grow gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION.CALL_PERMISSION.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('CONVERSATION.CALL_PERMISSION.DESCRIPTION') }}
          </span>
        </div>
      </div>

      <!-- Divider -->
      <div class="h-px bg-n-slate-5" />

      <!-- Response section -->
      <div class="flex flex-col items-center py-2.5 px-3 gap-0.5">
        <span
          class="text-sm font-medium"
          :class="isAccepted ? 'text-n-teal-11' : 'text-n-ruby-11'"
        >
          {{ responseLabel }}
        </span>
        <span
          v-if="isAccepted && !isPermanent && expirationText"
          class="text-[11px] text-n-slate-10"
        >
          {{
            $t('CONVERSATION.CALL_PERMISSION.EXPIRES', { time: expirationText })
          }}
        </span>
      </div>
    </div>
  </BaseBubble>
</template>
